import {
	CheckOutlined,
	FileTextOutlined,
	InboxOutlined,
	LeftOutlined,
	PlusOutlined,
	RightOutlined,
	SearchOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Button,
	Card,
	DatePicker,
	Input,
	InputNumber,
	message,
	Radio,
	Select,
	Steps,
	Upload,
} from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { getClients } from "@/modules/clients/services/clients.backend";
import { blocksApi } from "@/modules/objects/api/blocks.api";
import { buildingsApi } from "@/modules/objects/api/buildings.api";
import { roomsApi } from "@/modules/objects/api/rooms.api";
import type { AppRole } from "@/shared/config/navigation";
import {
	canCreateSale as canCreateSaleByRole,
	canGiveDiscount as canGiveDiscountByRole,
} from "@/shared/config/permissions";
import { parseApiError } from "@/shared/utils/error";
import { formatMoney } from "@/shared/utils/money";
import { submitSaleCreate } from "../services/sales.backend";

const { Dragger } = Upload;

const moneyInputProps = {
	formatter: (value: number | string | undefined) =>
		`${value ?? ""}`.replace(/\B(?=(\d{3})+(?!\d))/g, " "),
	parser: (value: string | undefined) => Number((value ?? "").replace(/\s/g, "")),
};

export function SaleCreatePage() {
	const navigate = useNavigate();
	const location = useLocation();
	const queryClient = useQueryClient();
	const [searchParams] = useSearchParams();
	const { role } = useOutletContext<{ role: AppRole }>();
	const canCreateSale = canCreateSaleByRole(role);
	const canDiscount = canGiveDiscountByRole(role);

	const fromDashboard = (location.state as any)?.fromDashboard === true;

	const [currentStep, setCurrentStep] = useState(0);

	// Wizard Form State
	const [selectedBuildingId, setSelectedBuildingId] = useState(
		(location.state as any)?.buildingId || searchParams.get("buildingId") || ""
	);
	const [selectedBlockId, setSelectedBlockId] = useState(
		(location.state as any)?.blockId || searchParams.get("blockId") || ""
	);
	const [selectedRoomId, setSelectedRoomId] = useState("");
	const [selectedClientId, setSelectedClientId] = useState("");
	const [clientSearch, setClientSearch] = useState("");
	const [paymentType, setPaymentType] = useState<"naqd" | "muddatli">("muddatli");
	const [basePrice, setBasePrice] = useState(0);
	const [discount, setDiscount] = useState(0);
	const [downPayment, setDownPayment] = useState(0);
	const [downPaymentPercent, setDownPaymentPercent] = useState(30);
	const [downPaymentMode, setDownPaymentMode] = useState<"percent" | "amount">("amount");
	const [monthsCount, setMonthsCount] = useState(36);
	// The selected date is the initial payment date (month 1); monthly payments
	// are generated starting one month after it
	const [startDate, setStartDate] = useState(dayjs());
	const requestedRoomId = (location.state as any)?.roomId || searchParams.get("roomId") || "";

	const { data: buildings = [] } = useQuery({
		queryKey: ["buildings"],
		queryFn: () => buildingsApi.getBuildings({ limit: 100 }),
	});

	const { data: blocks = [] } = useQuery({
		queryKey: ["blocks", selectedBuildingId],
		queryFn: () => blocksApi.getBlocks({ buildingId: selectedBuildingId, limit: 100 }),
		enabled: !!selectedBuildingId,
	});

	const { data: rooms = [] } = useQuery({
		queryKey: ["rooms", selectedBuildingId],
		queryFn: () => roomsApi.getRooms({ buildingId: selectedBuildingId, limit: 100 }),
		enabled: !!selectedBuildingId,
	});

	const { data: clientsResult } = useQuery({
		queryKey: ["clients", "sale-create", clientSearch],
		queryFn: () =>
			getClients({
				limit: 100,
				page: 1,
				...(clientSearch.trim() ? { search: clientSearch.trim() } : {}),
			}),
	});

	const clients = clientsResult?.items ?? [];

	useEffect(() => {
		if (selectedBuildingId || buildings.length === 0) {
			return;
		}
		setSelectedBuildingId(buildings[0]?.id ?? "");
	}, [buildings, selectedBuildingId]);

	useEffect(() => {
		if (!selectedBuildingId || blocks.length === 0) {
			return;
		}
		if (blocks.some((block) => block.id === selectedBlockId)) {
			return;
		}
		setSelectedBlockId(blocks[0]?.id ?? "");
		setSelectedRoomId("");
	}, [blocks, selectedBlockId, selectedBuildingId]);

	// 1. Available Buildings/Blocks/Rooms
	const availableBlocks = blocks;

	const availableRooms = useMemo(() => {
		return rooms.filter(
			(r) =>
				r.buildingId === selectedBuildingId &&
				r.blockId === selectedBlockId &&
				r.status === "sotuvda"
		);
	}, [rooms, selectedBuildingId, selectedBlockId]);

	const selectedRoom = useMemo(() => {
		return rooms.find((r) => r.id === selectedRoomId);
	}, [rooms, selectedRoomId]);

	const applyRoomPaymentDefaults = useCallback(
		(room: NonNullable<typeof selectedRoom>, type = paymentType) => {
			const cashPrice = room.finishedPrice || room.basePrice;
			const installmentPrice = room.installmentPrice || cashPrice;
			const nextBasePrice = type === "muddatli" ? installmentPrice : cashPrice;
			const minInitialPercent = room.minInitialPaymentPercent ?? 0;
			const months = room.installmentMonths || 12;
			// Minimum initial payment: the percent floor or one even month's share, whichever is larger
			const minInitial = Math.max(
				Math.round(nextBasePrice * (minInitialPercent / 100)),
				Math.round(nextBasePrice / months)
			);

			setBasePrice(nextBasePrice);
			setMonthsCount(months);
			setDownPaymentPercent(minInitialPercent);
			setDownPayment(type === "muddatli" ? minInitial : 0);
		},
		[paymentType]
	);

	useEffect(() => {
		if (!requestedRoomId || selectedRoomId || rooms.length === 0) {
			return;
		}

		const room = rooms.find((item) => item.id === requestedRoomId);
		if (!room) {
			return;
		}

		setSelectedRoomId(room.id);
		applyRoomPaymentDefaults(room);
	}, [requestedRoomId, rooms, selectedRoomId, applyRoomPaymentDefaults]);

	// Auto-set base price when room is selected
	const handleRoomChange = (roomId: string) => {
		setSelectedRoomId(roomId);
		const room = rooms.find((r) => r.id === roomId);
		if (room) {
			applyRoomPaymentDefaults(room);
		}
	};

	// 2. Client Selection options
	const selectedClient = useMemo(() => {
		return clients.find((c) => c.id === selectedClientId);
	}, [clients, selectedClientId]);

	// 3. Live Calculations
	const finalPrice = Math.max(0, basePrice - discount);
	console.log(selectedRoom);
	const minInitialPaymentPercent = selectedRoom?.minInitialPaymentPercent ?? 0;
	// Minimum initial payment: the percent floor or one even month's share, whichever is larger
	const minDownPayment =
		paymentType === "muddatli"
			? Math.max(
					Math.round(finalPrice * (minInitialPaymentPercent / 100)),
					monthsCount > 0 ? Math.round(finalPrice / monthsCount) : 0
				)
			: 0;

	// Calculate actual down payment value based on mode
	const actualDownPayment = useMemo(() => {
		if (paymentType === "naqd") {
			return 0;
		}
		if (downPaymentMode === "percent") {
			return Math.round(finalPrice * (downPaymentPercent / 100));
		}
		return downPayment;
	}, [paymentType, finalPrice, downPaymentMode, downPaymentPercent, downPayment]);

	const monthlyPayment = useMemo(() => {
		if (paymentType === "naqd") {
			return 0;
		}
		// Month 1 of the term is the initial payment; the rest spreads over monthsCount - 1
		const remaining = finalPrice - actualDownPayment;
		return monthsCount > 1 ? Math.round(remaining / (monthsCount - 1)) : 0;
	}, [paymentType, finalPrice, actualDownPayment, monthsCount]);

	// Wizard Navigation Checks
	const next = () => {
		if (currentStep === 0 && !selectedRoomId) {
			message.warning("Iltimos, xonani tanlang!");
			return;
		}
		if (currentStep === 1 && !selectedClientId) {
			message.warning("Iltimos, mijozni tanlang!");
			return;
		}
		if (currentStep === 2) {
			if (paymentType === "muddatli" && actualDownPayment >= finalPrice) {
				message.warning("Boshlang'ich to'lov yakuniy narxdan kichik bo'lishi kerak!");
				return;
			}
			if (paymentType === "muddatli" && actualDownPayment < minDownPayment) {
				message.warning(
					`Boshlang'ich to'lov minimal miqdordan (${formatMoney(minDownPayment)} UZS) kam bo'lishi mumkin emas!`
				);
				return;
			}
		}
		setCurrentStep(currentStep + 1);
	};

	const prev = () => {
		setCurrentStep(currentStep - 1);
	};

	// 5. Final Submit
	const createMutation = useMutation({
		mutationFn: submitSaleCreate,
		onSuccess: (savedSale) => {
			queryClient.invalidateQueries({ queryKey: ["rooms"] });
			if (selectedRoomId) {
				queryClient.invalidateQueries({ queryKey: ["room", selectedRoomId] });
			}
			queryClient.invalidateQueries({ queryKey: ["sales"] });
			message.success("Sotuv muvaffaqiyatli rasmiylashtirildi!");
			navigate(`/sales/${savedSale.id}`);
		},
		onError: (err: unknown) => {
			message.error(parseApiError(err) || "Sotuv yaratishda xatolik yuz berdi");
		},
	});

	const handleSubmit = async () => {
		if (!canCreateSale) {
			message.warning("Super Admin xona sotuvini yarata olmaydi.");
			return;
		}

		if (!(selectedRoom && selectedClient)) {
			return;
		}

		// Generate payment schedule lines (month 1 is the initial payment on startDate;
		// monthly payments begin one month after it)
		const scheduleLines: any[] = [];
		if (paymentType === "muddatli" && monthsCount > 1) {
			const remaining = finalPrice - actualDownPayment;
			const calculatedMonthly = Math.round(remaining / (monthsCount - 1));

			for (let i = 1; i <= monthsCount - 1; i++) {
				scheduleLines.push({
					index: i,
					date: startDate.add(i, "month").format("YYYY-MM-DD"),
					plannedAmount: calculatedMonthly,
					paidAmount: 0,
				});
			}
		}

		const newSaleData = {
			clientId: selectedClient.id,
			clientName: [selectedClient.lastName, selectedClient.firstName, selectedClient.patronymic]
				.filter(Boolean)
				.join(" "),
			phone: selectedClient.phone,
			buildingId: selectedRoom.buildingId,
			buildingName: buildings.find((b) => b.id === selectedRoom.buildingId)?.name || "",
			blockId: selectedRoom.blockId,
			blockName: blocks.find((b) => b.id === selectedRoom.blockId)?.name || "",
			roomId: selectedRoom.id,
			roomNumber: selectedRoom.roomNumber,
			floor: selectedRoom.floor,
			area: selectedRoom.area,
			basePrice,
			discount,
			finalPrice,
			downPayment: actualDownPayment,
			paymentType,
			monthsCount: paymentType === "muddatli" ? monthsCount : 1,
			startDate: startDate.format("YYYY-MM-DD"),
			monthlyPayment,
			managerName: selectedClient.managerName || "Sardor T.",
			schedule: scheduleLines,
		};

		createMutation.mutate(newSaleData);
	};

	return (
		<div className="crm-sale-create">
			<div className="crm-page-head">
				<div>
					<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
						{fromDashboard && (
							<Button
								type="text"
								icon={<LeftOutlined />}
								onClick={() => navigate(-1)}
								style={{ color: "var(--crm-text-secondary)" }}
							/>
						)}
						<h1>Yangi sotuv yaratish</h1>
					</div>
					<p>
						{fromDashboard ? (
							<span style={{ color: "var(--crm-text-secondary)" }}>Bosh sahifa / </span>
						) : null}
						Bosqichma-bosqich sotuv shartnomasi rasmiylashtirish
					</p>
				</div>
			</div>

			<div className="crm-card crm-sale-steps">
				<Steps
					current={currentStep}
					responsive={true}
					items={[
						{ title: "Xona", description: "Obyekt tanlash" },
						{ title: "Mijoz", description: "Mijoz biriktirish" },
						{ title: "To'lov turi", description: "Moliyaviy shartlar" },
						{ title: "Hujjatlar", description: "Fayl yuklash" },
						{ title: "Tasdiqlash", description: "Yakuniy tekshiruv" },
					]}
				/>
			</div>

			{/* Step 1: Room Selection */}
			{currentStep === 0 && (
				<Card title="1. Obyekt va xonani tanlash" className="crm-card crm-sale-step-card">
					<div className="crm-sale-form-grid">
						<div className="crm-sale-field">
							<span>Binoni tanlang</span>
							<Select
								value={selectedBuildingId}
								onChange={(val) => {
									setSelectedBuildingId(val);
									setSelectedBlockId("");
									setSelectedRoomId("");
								}}
								options={buildings.map((b) => ({ label: b.name, value: b.id }))}
								placeholder="Binoni tanlang"
								className="w-full"
							/>
						</div>
						<div className="crm-sale-field">
							<span>Blokni tanlang</span>
							<Select
								value={selectedBlockId}
								onChange={(val) => {
									setSelectedBlockId(val);
									setSelectedRoomId("");
								}}
								options={availableBlocks.map((b) => ({ label: b.name, value: b.id }))}
								placeholder="Blokni tanlang"
								disabled={!selectedBuildingId}
								className="w-full"
							/>
						</div>
					</div>

					<div className="crm-sale-field">
						<span>Xonani tanlang</span>
						<Select
							value={selectedRoomId}
							onChange={handleRoomChange}
							options={availableRooms.map((r) => ({
								label: `${r.roomNumber}-xona (${r.roomsCount}-xonali, ${r.area} m2, ${r.floor}-qavat) - ${formatMoney(r.basePrice)} UZS`,
								value: r.id,
							}))}
							placeholder="Bo'sh xonalar ro'yxati"
							disabled={!selectedBlockId || availableRooms.length === 0}
							className="w-full"
						/>
						{selectedBlockId && availableRooms.length === 0 && (
							<p className="text-red-500 text-xs mt-1">
								Ushbu blokda sotuvda bo'sh xonalar qolmagan.
							</p>
						)}
					</div>

					{selectedRoom && (
						<div className="mt-6 p-5 bg-[#f8fafd] border border-blue-100 rounded-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4 transition-all">
							<div className="flex items-center gap-4">
								<div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600">
									<svg
										className="w-6 h-6"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
										/>
									</svg>
								</div>
								<div>
									<h4 className="text-xs font-semibold text-slate-500 mb-1">Tanlangan xona</h4>
									<div className="text-lg font-extrabold text-slate-800 flex items-center gap-2">
										<span>{selectedRoom.roomNumber}-xona</span>
										<span className="text-xs font-bold px-2 py-0.5 bg-blue-100 text-blue-700 rounded-lg">
											{selectedRoom.roomsCount} xonali
										</span>
									</div>
									<div className="text-xs font-medium text-slate-400 mt-1 flex items-center gap-2">
										<span>{buildings.find((b) => b.id === selectedRoom.buildingId)?.name}</span>
										<span className="w-1 h-1 rounded-full bg-slate-300"></span>
										<span>{blocks.find((b) => b.id === selectedRoom.blockId)?.name}</span>
										<span className="w-1 h-1 rounded-full bg-slate-300"></span>
										<span>{selectedRoom.floor}-qavat</span>
										<span className="w-1 h-1 rounded-full bg-slate-300"></span>
										<span>{selectedRoom.area} m2</span>
									</div>
								</div>
							</div>
							<div className="text-right">
								<div className="text-xs font-bold text-slate-500 mb-1">Narxi</div>
								<div className="text-2xl font-black text-[#2756d8]">
									{formatMoney(selectedRoom.basePrice)} UZS
								</div>
							</div>
						</div>
					)}
				</Card>
			)}

			{/* Step 2: Client Selection */}
			{currentStep === 1 && (
				<Card
					title="2. Mijoz"
					extra={
						<Button icon={<PlusOutlined />} onClick={() => navigate("/clients/new")}>
							Yangi mijoz
						</Button>
					}
					className="crm-card crm-sale-step-card crm-sale-client-step"
				>
					<div className="mb-4">
						<Input
							placeholder="Mavjud mijozni qidirish"
							prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
							value={clientSearch}
							onChange={(e) => setClientSearch(e.target.value)}
							className="crm-sale-client-search"
						/>

						<div className="flex flex-col gap-2 mt-4">
							{clients.map((client) => {
								const isSelected = client.id === selectedClientId;
								const toneColors: Record<string, string> = {
									blue: "#3b82f6",
									green: "#22c55e",
									purple: "#a855f7",
									red: "#ef4444",
									amber: "#f59e0b",
								};
								const avatarBg = toneColors[client.tone || "blue"] || "#3b82f6";
								return (
									<button
										type="button"
										key={client.id}
										onClick={() => setSelectedClientId(client.id)}
										className={`flex items-center gap-4 w-full p-4 rounded-xl border-2 transition-all ${
											isSelected
												? "bg-blue-50 border-[#2756d8]"
												: "bg-[#f4f5f7] border-transparent hover:bg-slate-200"
										}`}
									>
										<div
											className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-xs shrink-0"
											style={{ backgroundColor: avatarBg }}
										>
											{client.initials}
										</div>
										<div className="flex-1 min-w-0 text-left">
											<span
												className={`block text-sm font-extrabold ${isSelected ? "text-[#2756d8]" : "text-slate-800"}`}
											>
												{client.lastName} {client.firstName} {client.patronymic || ""}
											</span>
											<span className="block text-xs font-semibold text-slate-500 mt-1">
												{client.phone}
												{client.passport?.seriesNumber ? ` - ${client.passport.seriesNumber}` : ""}
											</span>
										</div>
										<div className="shrink-0">
											{isSelected ? (
												<span className="text-[#2756d8] font-bold text-sm">
													<CheckOutlined className="mr-1" /> Tanlandi
												</span>
											) : (
												<span className="text-slate-700 font-bold text-sm">Tanlash</span>
											)}
										</div>
									</button>
								);
							})}
							{clients.length === 0 && (
								<div className="text-center py-8 text-gray-400">Mijozlar topilmadi</div>
							)}
						</div>
					</div>
				</Card>
			)}

			{/* Step 3: Payment Terms */}
			{currentStep === 2 && (
				<Card title="3. To'lov turi va moliyaviy shartlar" className="crm-card mb-4">
					<div className="mb-4">
						<div className="block text-sm font-semibold mb-2">To'lov shakli</div>
						<Radio.Group
							value={paymentType}
							onChange={(e) => {
								const nextPaymentType = e.target.value as "naqd" | "muddatli";
								setPaymentType(nextPaymentType);
								if (selectedRoom) {
									applyRoomPaymentDefaults(selectedRoom, nextPaymentType);
								}
							}}
							optionType="button"
							buttonStyle="solid"
						>
							<Radio.Button value="naqd">100% Naqd / O'tkazma</Radio.Button>
							<Radio.Button value="muddatli">Bo'lib to'lash (Rassrochka)</Radio.Button>
						</Radio.Group>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
						{canDiscount ? (
							<>
								<div>
									<div className="block text-sm font-semibold mb-1">
										Shartnoma bazaviy narxi (UZS)
									</div>
									<InputNumber
										value={basePrice}
										onChange={(val) => setBasePrice(val || 0)}
										className="w-full"
										{...moneyInputProps}
									/>
								</div>
								<div>
									<div className="block text-sm font-semibold mb-1">Chegirma (UZS)</div>
									<InputNumber
										value={discount}
										onChange={(val) => setDiscount(val || 0)}
										className="w-full"
										{...moneyInputProps}
									/>
								</div>
							</>
						) : (
							<div>
								<div className="block text-sm font-semibold mb-1">
									Shartnoma bazaviy narxi (UZS)
								</div>
								<InputNumber
									value={basePrice}
									onChange={(val) => setBasePrice(val || 0)}
									className="w-full"
									{...moneyInputProps}
								/>
							</div>
						)}
					</div>

					{paymentType === "muddatli" && (
						<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
							<div>
								<div className="block text-sm font-semibold mb-1">Boshlang'ich to'lov</div>
								<Radio.Group
									value={downPaymentMode}
									onChange={(e) => setDownPaymentMode(e.target.value)}
									className="mb-2"
								>
									<Radio.Button value="percent">Foiz</Radio.Button>
									<Radio.Button value="amount">Summa</Radio.Button>
								</Radio.Group>
								{downPaymentMode === "percent" ? (
									<InputNumber
										min={0}
										max={100}
										value={downPaymentPercent}
										onChange={(val) => setDownPaymentPercent(val || 0)}
										className="w-full"
										placeholder="30"
									/>
								) : (
									<InputNumber
										min={0}
										value={downPayment}
										onChange={(val) => setDownPayment(val || 0)}
										className="w-full"
										{...moneyInputProps}
									/>
								)}
								<div className="text-xs text-slate-400 mt-1">
									Minimal: {formatMoney(minDownPayment)} UZS
								</div>
							</div>
							<div>
								<div className="block text-sm font-semibold mb-1">Muddat (oylarda)</div>
								<InputNumber
									min={1}
									precision={0}
									value={monthsCount}
									disabled
									className="w-full"
								/>
								<div className="text-xs text-slate-400 mt-1">
									Xona uchun admin tomonidan belgilangan
								</div>
							</div>
							<div>
								<div className="block text-sm font-semibold mb-1">Boshlang'ich to'lov sanasi</div>
								<DatePicker
									value={startDate}
									onChange={(val) => val && setStartDate(val)}
									className="w-full"
									format="YYYY-MM-DD"
									allowClear={false}
								/>
							</div>
						</div>
					)}

					<div className="mt-6 p-5 bg-[#f8fafd] border border-blue-100 rounded-xl flex flex-col gap-3">
						<div className="flex justify-between items-center text-sm">
							<span className="text-slate-500 font-bold">Yakuniy shartnoma summasi:</span>
							<span className="font-extrabold text-slate-800">{formatMoney(finalPrice)} UZS</span>
						</div>
						{paymentType === "muddatli" && (
							<>
								<div className="flex justify-between items-center text-sm">
									<span className="text-slate-500 font-bold">Qoldiq muddatli summa:</span>
									<span className="font-extrabold text-slate-800">
										{formatMoney(Math.max(0, finalPrice - actualDownPayment))} UZS
									</span>
								</div>
								<div className="flex justify-between items-center border-t border-blue-200 pt-3 mt-1">
									<span className="font-bold text-slate-600">Oylik to'lov summasi:</span>
									<span className="text-xl font-black text-[#2756d8]">
										{formatMoney(monthlyPayment)} UZS / oy
									</span>
								</div>
							</>
						)}
					</div>
				</Card>
			)}

			{/* Step 4: Documents Upload */}
			{currentStep === 3 && (
				<Card title="4. Hujjatlar" className="crm-card crm-sale-step-card crm-sale-doc-step">
					<div className="crm-sale-doc-grid">
						{["Shartnoma .docx/.pdf", "To'lov grafigi .xlsx", "Rozilik xati (ixtiyoriy)"].map(
							(label) => (
								<Dragger
									key={label}
									multiple={false}
									className="crm-sale-doc-upload"
									onChange={(info) => {
										if (info.file.status === "done") {
											message.success(`${info.file.name} muvaffaqiyatli yuklandi.`);
										}
									}}
								>
									<InboxOutlined />
									<span>{label}</span>
								</Dragger>
							)
						)}
					</div>
					<div className="crm-sale-doc-note">
						<FileTextOutlined />
						<span>
							Shartnoma avtomatik generatsiya bo'ladi (contract_template.docx) - keyin
							almashtirsangiz bo'ladi.
						</span>
					</div>
				</Card>
			)}

			{/* Step 5: Summary */}
			{currentStep === 4 && (
				<Card title="5. Tasdiqlash" className="crm-card crm-sale-step-card crm-sale-confirm-step">
					<div className="crm-sale-confirm-grid">
						<div className="crm-sale-confirm-panel">
							<div className="crm-sale-confirm-row">
								<span>Mijoz</span>
								<span>
									{[selectedClient?.lastName, selectedClient?.firstName, selectedClient?.patronymic]
										.filter(Boolean)
										.join(" ")}
								</span>
							</div>
							<div className="crm-sale-confirm-row">
								<span>Xona</span>
								<span>
									{buildings.find((b) => b.id === selectedRoom?.buildingId)?.name} -{" "}
									{selectedRoom?.roomNumber} - {selectedRoom?.area} m2
								</span>
							</div>
							<div className="crm-sale-confirm-row">
								<span>To'lov turi</span>
								<span>
									{paymentType === "muddatli" ? `Muddatli - ${monthsCount} oy` : "100% Naqd"}
								</span>
							</div>
						</div>
						<div className="crm-sale-confirm-panel">
							<div className="crm-sale-confirm-row">
								<span>Yakuniy summa</span>
								<span>{formatMoney(finalPrice)} UZS</span>
							</div>
							<div className="crm-sale-confirm-row">
								<span>Boshlang'ich</span>
								<span>
									{paymentType === "muddatli"
										? `${formatMoney(actualDownPayment)} UZS`
										: "To'liq to'lov"}
								</span>
							</div>
							{paymentType === "muddatli" && (
								<div className="crm-sale-confirm-row">
									<span>Oylik</span>
									<span>{formatMoney(monthlyPayment)} UZS</span>
								</div>
							)}
						</div>
					</div>

					<div className="crm-sale-confirm-note">
						Sotuvni yakunlashingiz bilan: xona holati avtomatik <b>band</b> (rassrochka bo'lsa) yoki{" "}
						<b>sotilgan</b> holatiga o'tadi, to'lov grafigi shakllanadi va mijozga "Xush kelibsiz"
						tabrik SMSi yuboriladi.
					</div>
				</Card>
			)}

			{/* Footer Actions */}
			<div className="crm-card crm-sale-footer">
				<Button icon={<LeftOutlined />} onClick={prev} disabled={currentStep === 0}>
					Orqaga
				</Button>

				<span className="text-xs text-gray-400">Qadam {currentStep + 1} / 5</span>

				{currentStep < 4 ? (
					<Button
						type="primary"
						className="crm-primary-btn"
						icon={<RightOutlined />}
						onClick={next}
					>
						Davom etish
					</Button>
				) : (
					<Button
						type="primary"
						style={{ backgroundColor: "var(--crm-green)", borderColor: "var(--crm-green)" }}
						icon={<CheckOutlined />}
						onClick={handleSubmit}
						loading={createMutation.isPending}
					>
						Shartnomani tasdiqlash
					</Button>
				)}
			</div>
		</div>
	);
}

export default SaleCreatePage;
